<template lang="pug">

.vuse
  section.vuse-intro
    AppTitle.u-mb-sm(:title="$t('mantra')")

    AppLink(to="https://baianat.github.io/vuse/example.html" class="button-link") {{$t('demo')}}

    img.vuse-intro-img( src='https://cdn.baianat.com/static/resources/vuse/1.png', srcset='https://cdn.baianat.com/static/resources/vuse/1.png 1x, https://cdn.baianat.com/static/resources/vuse/1@2x.png 2x')

  section.vuse-templates
    .vuse-templates-img
      img( src='https://cdn.baianat.com/static/resources/vuse/5.png')

    AppTitle.u-textCenter.u-mb-sm(tag="h2" :title="$t('templates')")

    .grid.is-desktop-4.is-tablet-6
      .column.u-mb-sm
        img(src="https://cdn.baianat.com/static/resources/vuse/3.png")
      .column.u-mb-sm
        img(src="https://cdn.baianat.com/static/resources/vuse/2.png")
      .column.u-mb-sm
        img(src="https://cdn.baianat.com/static/resources/vuse/4.png")



  section.vuse-features.u-mt-lg
    .grid.is-ultrawide-8.is-widescreen-9.is-desktop-10.is-center
      .column
        h2.u-mb-mi {{$t('features')}}
        .grid.is-tablet-6
          .column.u-mb-sm(v-for="item in $t('featuresItems')")
            h4 {{item.title}}
            p {{item.desc}}


  section.vuse-features.u-mt-lg
    .grid.is-ultrawide-8.is-widescreen-9.is-desktop-10.is-center
      .column
        .grid.is-mb.is-middle
          .column.is-desktop-6
            img( src='https://cdn.baianat.com/static/resources/vuse/6.png', srcset='https://cdn.baianat.com/static/resources/vuse/6.png 1x, https://cdn.baianat.com/static/resources/vuse/6@2x.png 2x')
          .column.is-desktop-6
            h4 {{$t('flexible')}}
            p {{$t('flexibleDesc')}}


  section.vuse-cta.u-mt-lg
    AppTitle(:title="$t('cta')" tag="h2")
    AppLink(to="https://baianat.github.io/vuse/getting-started.html" class="button-link is-white") {{$t('getStarted')}}



  .u-mb-md

</template>
<style lang="stylus">
$clMain = #2e3c48
$clPurple = #a26cd6

.app.is-vuse
  overflow: hidden

  &.is-dark
    .header.is-scroll
      background: $clMain

.vuse
  &-intro
    padding-top: unitRes(200, 400)
    padding-bottom: unitRes(120, 240)
    margin-left: -12vw
    margin-right: -12vw
    padding-right: 8vw
    padding-left: 8vw
    z-index: 0
    position: relative
    background-color: $clMain
    background-repeat: no-repeat
    color: $clWhite

    &-img
      position: absolute
      top: 20%
      right: 13%
      z-index: -1

      +mq($until: 'ultrawide')
        max-width: 600px
        top: 30%
        right: 10%

      +mq($until: 1500px)
        max-width: 400px
        top: 40%
        right: 8%

      +mq($until: 'desktop')
        position: static
        max-width: 100%

  &-templates
    position: relative
    z-index: 2
    padding-top: unitRes(80, 200)

    &-img
      position: absolute
      margin-left: -12vw
      margin-right: -12vw
      top: 0
      right: 0
      left: 0

      img
        width: 100%

  &-cta
    background-color: $clPurple
    padding: unitRes(20, 160)
    color: $clWhite
    background-image: url('https://cdn.baianat.com/static/resources/vuse/7.png')
    background-repeat: no-repeat
    background-position: 100% center
    background-attachment: fixed
    text-shadow: 1px 1px 11px alpha($clBlack, 0.2)

html[lang^='ar']
  .vuse
    &-cta
      background-position: 0% center

    &-intro
      &-img
        right: auto
        left: 13%

        +mq($until: 'ultrawide')
          right: auto
          left: 10%

        +mq($until: 1500px)
          right: auto
          left: 8%
</style>

<script>
import Prism from '~/components/Prism';
import { generateSeoMeta } from '~/utils/seo';

export default {
  scrollToTop: true,
  meta: {
    theme: 'is-vuse is-fixedHeader is-whiteHeader',
    logoText: 'logoVuseTypo',
    logoIcon: 'logoVuseIcon'
  },

  head: generateSeoMeta(
    {
      title: 'pageTitle',
      description: 'pageDesc'
    },
    { i18n: true }
  ),
  components: {
    Prism
  },
  data: () => ({
    install: `npm install verte
# or use yarn
yarn add verte

# Usage

<template>
  <verte picker="square" model="rgb"></verte>
</template>

<script>
  import Verte from 'verte';
  import 'verte/dist/verte.css';
  // register component globally
  Vue.component(Verte.name, Verte);

  new Vue ({
    el: '#app',
    // OR register locally
    components: { Verte }
  });
<\/script>
`
  })
};
</script>

<i18n>
{
  "en": {
    "pageTitle":"Vuse",
    "pageDesc":"Advanced page and email builder based on Vue.js. Vuse was developed to be easily integrated and to support you with mock UI.",
    "mantra":"Fully reactive |page builder",
    "demo":"Demo",
    "templates":"Choose from different |templates",
    "features":"Features",
    "featuresItems":{
      "1":{
        "title":"Quick integration",
        "desc":"Easily integrate Vuse into your project as a Vue plugin. No separate packages needed."
      },
      "2":{
        "title":"Take control with sections",
        "desc":"Vuse sections are fully editable and customizable to give you more flexibility with the building process."
      },
      "3":{
        "title":"The styler directive",
        "desc":"A smart, automatically-injected Vue directive that supports different HTML elements to make editing elements a lot easier."
      },
      "4":{
        "title":"Built-in data seeder",
        "desc":"Set customizable seeders to supply your UI with the mock data you need during your building process"
      }
    },
    "flexible":"Flexible exporting",
    "flexibleDesc":"Export your built pages with ease. Choose from 3 different export formats; JSON, PWA or Preview.",
    "cta":"Start building |with Vuse",
    "getStarted":"Get started"
  },
  "ar": {
    "pageTitle":"Vuse",
    "pageDesc":"Vuse مُنشأ صفحات متقدم  يعتمد على Vue.js. تم تطوير Vuse ليتم دمجه بسهولة عن طريق واجهة مستخدم وهمية تظهر أثناء عملية البناء.",
    "mantra":"أداة بناء صفحات |تفاعلية بالكامل.",
    "demo":"عرض",
    "templates":"اختار من بين |القوالب المختلفة",
    "features":"المميزات",
    "featuresItems":{
      "1":{
        "title":"التكامل السريع",
        "desc":"لن تحتاج إلى حزم منفصلة بعد الآن، يمكنك دمج Vuse بسهولة في مشروعك كمكون إضافي."
      },
      "2":{
        "title":"السيطرة على الأقسام",
        "desc":"أقسام Vuse قابلة للتعديل والتخصيص، لتمنحك المزيد من المرونة في عملية البناء."
      },
      "3":{
        "title":"توجيه الأسلوب",
        "desc":"توجيه Vuse ذكي ومحقن تلقائيًا، ويدعم عناصر HTML المختلفة، لتصبح عناصر التعديل أسهل كثيرًا."
      },
      "4":{
        "title":"مغذي بيانات مرفق",
        "desc":"يمكنك تعيين ملحقات مخصصة لتزويد واجهة المستخدم بالبيانات الوهمية التي تحتاج إليها أثناء عملية البناء."
      }
    },
    "flexible":"عملية تصدير مرنة",
    "flexibleDesc":"يمكنك تصدير الصفحات المبنية بكل سهولة. اختر من بين 3 تنسيقات تصدير مختلفة JSON أو PWA أو Preview.",
    "cta":"صمم صفحاتك |مع Vuse. ",
    "getStarted":"ابدأ الآن!"
  }
}
</i18n>
